import { isRowsPerPage } from '../../components/RowsPerPageSelect';
import { REPORT_SECTIONS, type SectionId } from '../ProgressReport/flow';
import { BENCHMARK_FILTERS, DEFAULT_QUERY, type ListQuery } from './listRows';

export const LIST_STATE_KEY = 'bamboo.progressReports.v1';
export const PRINT_SECTIONS_KEY = 'bamboo.progressReports.printSections.v1';

export type ListState = { query: ListQuery; selected: string[] };

type StateStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function isQuery(value: unknown): value is ListQuery {
  if (typeof value !== 'object' || value === null) return false;
  const q = value as Record<string, unknown>;
  return (
    (q.school === null || typeof q.school === 'string') &&
    (q.grade === undefined || typeof q.grade === 'string') &&
    typeof q.teacher === 'string' &&
    (q.class === undefined || typeof q.class === 'string') &&
    typeof q.benchmark === 'string' &&
    (BENCHMARK_FILTERS as readonly string[]).includes(q.benchmark) &&
    typeof q.search === 'string' &&
    (q.sort === 'asc' || q.sort === 'desc') &&
    typeof q.page === 'number' &&
    Number.isInteger(q.page) &&
    q.page >= 1 &&
    (q.rowsPerPage === undefined || isRowsPerPage(q.rowsPerPage))
  );
}

function isState(value: unknown): value is ListState {
  if (typeof value !== 'object' || value === null) return false;
  const s = value as Record<string, unknown>;
  return isQuery(s.query) && Array.isArray(s.selected) && s.selected.every((id) => typeof id === 'string');
}

class MemoryStateStorage implements StateStorage {
  private items = new Map<string, string>();

  getItem(key: string): string | null {
    return this.items.get(key) ?? null;
  }

  setItem(key: string, value: string): void {
    this.items.set(key, value);
  }

  removeItem(key: string): void {
    this.items.delete(key);
  }

  clear(): void {
    this.items.clear();
  }
}

const memory = new MemoryStateStorage();

export function resetListState(): void {
  memory.clear();
}

function defaultStorage(): StateStorage {
  return memory;
}

export function loadListState(storage: StateStorage | null = defaultStorage()): ListState | null {
  try {
    const raw = storage?.getItem(LIST_STATE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isState(parsed)) return null;
    return { query: { ...DEFAULT_QUERY, ...parsed.query }, selected: [...parsed.selected] };
  } catch {
    return null;
  }
}

export function saveListState(state: ListState, storage: StateStorage | null = defaultStorage()): void {
  try {
    storage?.setItem(LIST_STATE_KEY, JSON.stringify(state));
  } catch {
    return;
  }
}

export function toggleSelected(selected: string[], id: string): string[] {
  return selected.includes(id) ? selected.filter((other) => other !== id) : [...selected, id];
}

export function withSelected(selected: string[], ids: string[]): string[] {
  return [...new Set([...selected, ...ids])];
}

export function withoutSelected(selected: string[], ids: string[]): string[] {
  const drop = new Set(ids);
  return selected.filter((id) => !drop.has(id));
}

export function hiddenSelectedCount(selected: string[], visibleIds: string[]): number {
  const visible = new Set(visibleIds);
  return selected.filter((id) => !visible.has(id)).length;
}

export function bannerLabel({ selected, visible, hidden }: { selected: number; visible: number; hidden: number }): string {
  const shown = selected - hidden;
  const head =
    visible > 0 && shown >= visible
      ? `All ${visible} students selected`
      : `${selected} ${selected === 1 ? 'student' : 'students'} selected`;
  return hidden > 0 ? `${head} · ${hidden} hidden by filters` : head;
}

type PrintSectionsStore = Record<string, { off: string[] }>;

function browserStorage(): StateStorage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

function readPrintSectionsStore(storage: StateStorage | null): PrintSectionsStore {
  try {
    const raw = storage?.getItem(PRINT_SECTIONS_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
    const store: PrintSectionsStore = {};
    for (const [owner, entry] of Object.entries(parsed as Record<string, unknown>)) {
      const off = (entry as { off?: unknown } | null)?.off;
      if (Array.isArray(off) && off.every((id) => typeof id === 'string')) store[owner] = { off };
    }
    return store;
  } catch {
    return {};
  }
}

export function loadPrintSections(owner: string, storage: StateStorage | null = browserStorage()): Set<SectionId> {
  const off = new Set(readPrintSectionsStore(storage)[owner]?.off ?? []);
  return new Set(REPORT_SECTIONS.map((section) => section.id).filter((id) => !off.has(id)));
}

export function savePrintSections(owner: string, chosen: ReadonlySet<SectionId>, storage: StateStorage | null = browserStorage()): void {
  const off = REPORT_SECTIONS.map((section) => section.id).filter((id) => !chosen.has(id));
  try {
    storage?.setItem(PRINT_SECTIONS_KEY, JSON.stringify({ ...readPrintSectionsStore(storage), [owner]: { off } }));
  } catch {
    return;
  }
}
